import {
  combineLatestTyped,
  filterNotUndefined,
  IList,
  IMap
} from 'annotation-tools/utils';
import { Observable, ReplaySubject } from 'rxjs';
import {
  distinctUntilChanged,
  groupBy,
  map,
  mergeMap,
  scan,
  shareReplay
} from 'rxjs/operators';
import { FetchErrorType } from '../../../api/types/common/fetch-error-type';
import { FetchResultType } from '../../../api/types/common/fetch-result';
import { token$ } from '../../auth-state';
import {
  ListWithPromiseState,
  listWithPromiseStateEq,
  listWithPromiseStateInit
} from './list-with-status';
import { attachPromiseStateToList } from './rxjs-operator/attach-promise-state-to-list';

type IdPair = IList<number>;
const genIdPair = (a: number, b: number) => IList<number>([a, b]);

/**
 * @description
 * refetch関数の実行時に発火させるためのstreamを生成
 */
export const fetchIdsStreamGenerator = () => new ReplaySubject<IdPair>(1);

/**
 * @description
 * refetch関数を生成
 */
export const fetchListFnGenerator = (fetchSignal$: ReplaySubject<IdPair>) => (
  id1: number,
  id2: number
) => {
  fetchSignal$.next(genIdPair(id1, id2));
};

type TwoIdListReducerState<T> = {
  idPair: IdPair;
  list: ListWithPromiseState<T>;
};

/**
 * @description
 * fetchListOf(id)を実行すると
 * allLists$のidの要素を更新する
 */
export const updateStreamGenerator = <T>(
  fetchSignal$: ReplaySubject<IdPair>,
  fetchFn: (
    token: string
  ) => (
    id1: number,
    id2: number
  ) => Promise<FetchResultType<IList<T>, FetchErrorType<void>>>
): Observable<TwoIdListReducerState<T>> =>
  combineLatestTyped(
    token$.pipe(filterNotUndefined()),
    fetchSignal$.asObservable()
  ).pipe(
    groupBy(([_, idPair]) => idPair),
    mergeMap(group =>
      group.pipe(
        map(([token, idPair]) =>
          fetchFn(token)(idPair.get(0) ?? 0, idPair.get(1) ?? 0)
        ),
        attachPromiseStateToList('mergeMap'),
        map(list => ({ idPair: group.key, list }))
      )
    ),
    shareReplay(1)
  );

export const twoIdListSetReducerGenerator = <T>() => (
  acc: IMap<IdPair, ListWithPromiseState<T>>,
  { idPair, list }: TwoIdListReducerState<T>
) => acc.set(idPair, list);

/**
 * @description
 * すべてのlistの集合．必要になったものからfetchして蓄える．
 * 複数のcomponentで共有するため．
 */
export const twoIdListSetStreamGenerator = <T>(
  update$: Observable<TwoIdListReducerState<T>>,
  allListsReducer: (
    acc: IMap<IdPair, ListWithPromiseState<T>>,
    update: TwoIdListReducerState<T>
  ) => IMap<IdPair, ListWithPromiseState<T>>
): Observable<IMap<IdPair, ListWithPromiseState<T>>> =>
  update$.pipe(
    scan(allListsReducer, IMap<IdPair, ListWithPromiseState<T>>()),
    shareReplay(1)
  );

export const listOfIdFnGenerator = <T>(
  allLists$: Observable<IMap<IdPair, ListWithPromiseState<T>>>
) => (id1: number, id2: number): Observable<ListWithPromiseState<T>> =>
  allLists$.pipe(
    map(list => list.get(genIdPair(id1, id2)) ?? listWithPromiseStateInit<T>()),
    distinctUntilChanged(listWithPromiseStateEq),
    shareReplay(1)
  );
